import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Progress. Determinate bar: muted pill track, color-variant
 * fill driven by an inline width %. Mirrors .progress / .progress__fill in
 * the showcase ds.css (6px height, pill radius, no text).
 */
const progressFillVariants = cva('h-full rounded-full transition-[width] duration-300', {
  variants: {
    variant: {
      blue: 'bg-sober-primary',
      green: 'bg-sober-green-fg',
      yellow: 'bg-sober-yellow-fg',
      red: 'bg-sober-red',
      purple: 'bg-sober-purple',
    },
  },
  defaultVariants: { variant: 'blue' },
});

export interface ProgressProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressFillVariants> {
  value: number;
  max?: number;
}

export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, max = 100, variant, ...props }, ref) => {
    const safeMax = max > 0 ? max : 100;
    const pct = Math.min(100, Math.max(0, (value / safeMax) * 100));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={value}
        className={cn('h-1.5 w-full overflow-hidden rounded-full bg-sober-muted', className)}
        {...props}
      >
        <div className={cn(progressFillVariants({ variant }))} style={{ width: `${pct}%` }} />
      </div>
    );
  }
);
Progress.displayName = 'Progress';

export { progressFillVariants };
